import React, { useState, useMemo } from "react";
import CustomPrimaryButton from "../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import CustomButtonWithIcon from "../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import { dentalIcons } from "../../../dentail-indetifcation/utils-icons/dentalIcons";

function evaluateExpression(expr) {
  const cleaned = expr.replace(/\s+/g, "");
  if (!cleaned) return 0;

  // Validate characters
  if (/[^0-9.+\-*/]/.test(cleaned)) throw new Error("Invalid characters");

  // Handle leading + or - as part of the first number
  let i = 0;
  const tokens = [];
  let number = "";

  const pushNumber = () => {
    if (number === "" || number === "." || number === "+." || number === "-.") {
      throw new Error("Malformed number");
    }
    tokens.push(parseFloat(number));
    number = "";
  };

  while (i < cleaned.length) {
    const ch = cleaned[i];

    if (/[0-9.]/.test(ch)) {
      number += ch;
      i++;
      continue;
    }

    if ("+-*/".includes(ch)) {
      if (tokens.length === 0 && (ch === "+" || ch === "-") && number === "") {
        // leading sign for first number
        number = ch;
        i++;
        continue;
      }
      // close number
      pushNumber();
      tokens.push(ch);
      i++;
      continue;
    }

    // unreachable by validation
    i++;
  }
  if (number !== "") pushNumber();

  // now tokens like [num, op, num, op, num, ...]
  if (tokens.length === 0) return 0;

  // pass 1: * and /
  const pass1 = [];
  let idx = 0;
  pass1.push(tokens[idx++]); // first number
  while (idx < tokens.length) {
    const op = tokens[idx++];
    const right = tokens[idx++];

    if (op === "*" || op === "/") {
      const left = pass1.pop();
      if (op === "*") pass1.push(left * right);
      else {
        if (right === 0) throw new Error("Division by zero");
        pass1.push(left / right);
      }
    } else {
      pass1.push(op, right);
    }
  }

  // pass 2: + and -
  let result = pass1[0];
  idx = 1;
  while (idx < pass1.length) {
    const op = pass1[idx++];
    const right = pass1[idx++];
    if (op === "+") result += right;
    else result -= right;
  }
  return result;
}

const Calculator = ({ onResult, setShowCalculator }) => {
  const [expr, setExpr] = useState("");
  const [error, setError] = useState("");

  const displayValue = useMemo(() => (expr === "" ? "0" : expr), [expr]);

  const push = (ch) => {
    setError("");
    setExpr((s) => {
      // prevent duplicate operators (except allowing leading -/+)
      const last = s.at(-1);
      if ("+-*/".includes(ch)) {
        if (!s) {
          // allow leading + or - only
          return ch === "+" || ch === "-" ? ch : s;
        }
        if (last && "+-*/".includes(last)) {
          // replace last operator
          return s.slice(0, -1) + ch;
        }
      }
      if (ch === ".") {
        // avoid double dots in the current number segment
        const seg = s.split(/[-+*/]/).pop() || "";
        if (seg.includes(".")) return s;
        if (seg === "" || seg === "+" || seg === "-") return s + "0.";
      }
      return s + ch;
    });
  };

  const clear = () => {
    setError("");
    setExpr("");
  };

  const backspace = () => {
    setError("");
    setExpr((s) => s.slice(0, -1));
  };

  const calculate = () => {
    try {
      const val = evaluateExpression(expr || "0");
      const out =
        Math.abs(val) < 1e12 ? Number(val.toFixed(6)).toString() : String(val);
      setExpr(out);
      setError("");
      //   onResult?.(val);
    } catch (e) {
      setError(e.message || "Error");
    }
  };

  return (
    <div className="w-full select-none rounded-2xl p-1">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#717784] font-semibold font-semibold text-[16px]">
          <img src={dentalIcons.calculatorIcon} className="h-6 w-6" />
          Calculator
        </div>
        <div onClick={setShowCalculator} className="cursor-pointer">
          <img src={dentalIcons.backCalculator} className="h-6 w-6" />
        </div>
      </div>

      <div className="mb-3">
        <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right text-slate-800 text-xl font-medium tracking-wide">
          {displayValue}
        </div>
        {error && (
          <div className="mt-1 text-right text-sm text-rose-600">{error}</div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        {/* 7 8 9 / */}
        {[7, 8, 9].map((n) => (
          <button
            key={n}
            className="h-12 rounded-[8px] bg-slate-100 text-[#717784] font-semibold hover:bg-slate-200"
            onClick={() => push(String(n))}
          >
            {n}
          </button>
        ))}
        <button
          className="h-12 rounded-[8px] bg-[#DBF5FF] text-[#0A9FD4] font-semibold  hover:bg-sky-200"
          onClick={() => push("/")}
        >
          /
        </button>

        {/* 4 5 6 * */}
        {[4, 5, 6].map((n) => (
          <button
            key={n}
            className="h-12 rounded-[8px] bg-slate-100 text-[#717784] font-semibold hover:bg-slate-200"
            onClick={() => push(String(n))}
          >
            {n}
          </button>
        ))}
        <button
          className="h-12 rounded-[8px] bg-[#DBF5FF] text-[#0A9FD4] font-semibold  hover:bg-sky-200"
          onClick={() => push("*")}
        >
          *
        </button>

        {/* 1 2 3 - */}
        {[1, 2, 3].map((n) => (
          <button
            key={n}
            className="h-12 rounded-[8px] bg-slate-100 text-[#717784] font-semibold hover:bg-slate-200"
            onClick={() => push(String(n))}
          >
            {n}
          </button>
        ))}
        <button
          className="h-12 rounded-[8px] bg-[#DBF5FF] text-[#0A9FD4] font-semibold   hover:bg-sky-200"
          onClick={() => push("-")}
        >
          -
        </button>

        {/* 0 . + */}
        <button
          className="col-span-2 h-12 rounded-[8px] bg-slate-100 text-[#717784] font-semibold hover:bg-slate-200"
          onClick={() => push("0")}
        >
          0
        </button>
        <button
          className="h-12 rounded-[8px] bg-slate-100 text-[#717784] font-semibold hover:bg-slate-200"
          onClick={() => push(".")}
        >
          .
        </button>
        <button
          className="h-12 rounded-[8px] bg-[#DBF5FF] text-[#0A9FD4] font-semibold hover:bg-sky-200"
          onClick={() => push("+")}
        >
          +
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        <CustomPrimaryButton
          title="Calculate"
          onClick={calculate}
          isRound={true}
          width={"80%"}
        />

        <CustomButtonWithIcon
          title="C"
          onClick={clear}
          isRound={true}
          width={"10%"}
        />
      </div>
    </div>
  );
};
export default Calculator;
